import { Box } from '@breathelife/mui';
import { ReactElement, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ESignCeremonyStatus, Permission, User } from '@breathelife/types';

import { SubmitButton } from '../../../Components/Button/SubmitButton';
import { RestrictedToUserPermissions } from '../../../Components/Restricted/RestrictedToUserPermissions';
import Typography from '../../../Components/Typography';
import { CarrierContext } from '../../../Context/CarrierContext';
import { useNavigation, useSelector } from '../../../Hooks';
import { ModalLayout } from '../../../Layouts/Modal/ModalLayout';
import { Lead } from '../../../Models/Lead';
import { generateLeadsListUrl } from '../../../Navigation/Urls';
import { useUpdateLeadArchiveMutation } from '../../../ReactQuery/Lead/lead.mutations';

export type Props = {
  isOpen: boolean;
  closeModal: () => void;
  lead: Lead;
};

export function LeadArchiveConfirmationModal(props: Props): ReactElement {
  const { t } = useTranslation();
  const { features } = useContext(CarrierContext);
  const { leadTab } = useNavigation();
  const navigate = useNavigate();
  const { isOpen, closeModal, lead } = props;

  const activeESignCeremonyStatuses = [
    ESignCeremonyStatus.IN_PROGRESS,
    ESignCeremonyStatus.READY,
    ESignCeremonyStatus.SENT,
  ];

  const currentUser = useSelector((store) => store.leadPlatform.authentication.user) as User;

  let isSignatureCeremonyInProgress;

  // If lead is already archieved then do not check if signature ceremony is in progress
  if (!lead.archived) {
    isSignatureCeremonyInProgress =
      !lead.archived &&
      lead?.applications?.[0]?.eSignCeremonies.some((ceremony) =>
        activeESignCeremonyStatuses.includes(ceremony.status),
      );
  }

  const updateLeadArchiveMutation = useUpdateLeadArchiveMutation({
    onSuccess: () => {
      navigate(generateLeadsListUrl(leadTab));
      closeModal();
    },
  });

  function updateLeadArchive(): void {
    updateLeadArchiveMutation.mutate({ leadId: lead.id, archive: !lead.archived });
  }

  const title = lead.archived
    ? t(
        features.leadCreation.enabled
          ? 'modals.leadsArchive.unarchiveLeadTitle'
          : 'modals.leadsArchive.unarchiveApplicationTitle',
      )
    : t(
        features.leadCreation.enabled
          ? 'modals.leadsArchive.archiveLeadTitle'
          : 'modals.leadsArchive.archiveApplicationTitle',
      );

  return (
    <ModalLayout
      maxWidth='sm'
      isOpen={isOpen}
      closeModal={closeModal}
      title={title}
      submitButton={
        <RestrictedToUserPermissions requiredPermissions={[Permission.LeadArchive]}>
          <SubmitButton
            isLoading={updateLeadArchiveMutation.isLoading}
            disabled={updateLeadArchiveMutation.isLoading || isSignatureCeremonyInProgress}
            onClick={updateLeadArchive}
            data-testid='lead-archive-confirm'
          >
            {lead.archived ? t('cta.unarchive') : t('cta.archive')}
          </SubmitButton>
        </RestrictedToUserPermissions>
      }
    >
      <Box mt={2} mb={2.5}>
        {currentUser?.auth0Id !== lead.assignedToId && (
          <Typography display='block' variant='body1' grey={90}>
            {t(
              features.leadCreation.enabled
                ? 'modals.leadsArchive.leadNotAssignedToYou'
                : 'modals.leadsArchive.applicationNotAssignedToYou',
            )}
          </Typography>
        )}
        {isSignatureCeremonyInProgress ? (
          <Typography display='block' variant='body1' grey={90}>
            {features.leadCreation.enabled
              ? t('modals.leadsArchive.errorOnLeadArchiving')
              : t('modals.leadsArchive.errorOnApplicationArchiving')}
          </Typography>
        ) : (
          <Typography display='block' variant='body1' grey={90}>
            {lead.archived
              ? t(
                  features.leadCreation.enabled
                    ? 'modals.leadsArchive.unarchiveLeadConfirmationQuestion'
                    : 'modals.leadsArchive.unarchiveApplicationConfirmationQuestion',
                )
              : t(
                  features.leadCreation.enabled
                    ? 'modals.leadsArchive.archiveLeadConfirmationQuestion'
                    : 'modals.leadsArchive.archiveApplicationConfirmationQuestion',
                )}
          </Typography>
        )}
      </Box>
    </ModalLayout>
  );
}
